/**
 * Utility functions for file upload operations
 */

interface ApiService {
  confirmUploaded: (statusAccountId: string) => Promise<void>
  confirmUpdated: (statusAccountId: string, fileName: string, contentType: string) => Promise<void>
}

/**
 * Uploads a file to S3 using a presigned URL
 * @param file - The file to upload
 * @param uploadUrl - The presigned URL for upload
 * @param contentType - The content type of the file (optional, defaults to file.type)
 * @returns Promise<Response> - The fetch response
 */
export const uploadFileToS3 = async (
  file: File,
  uploadUrl: string,
  contentType?: string,
): Promise<Response> => {
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type,
    },
    body: file,
  })

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed with status: ${uploadResponse.status}`)
  }

  return uploadResponse
}

/**
 * Uploads a file to S3 and confirms the upload
 * @param file - The file to upload
 * @param uploadUrl - The presigned URL for upload
 * @param statusAccountId - The status account ID for confirmation
 * @param apiService - The API service instance
 * @param contentType - The content type of the file (optional, defaults to file.type)
 * @returns Promise<void>
 */
export const uploadFileToS3AndConfirm = async (
  file: File,
  uploadUrl: string,
  statusAccountId: string,
  apiService: ApiService,
  contentType?: string,
): Promise<void> => {
  try {
    // Upload file to S3
    const uploadResponse = await uploadFileToS3(file, uploadUrl, contentType)

    console.log(`File ${file.name} Upload Response:`, uploadResponse)
    console.log(`File ${file.name} Upload Status:`, uploadResponse.status)
    console.log(`File ${file.name} Upload OK:`, uploadResponse.ok)

    // Confirm upload was successful
    await apiService.confirmUploaded(statusAccountId)
    console.log(`File ${file.name} upload confirmed`)
  } catch (uploadError) {
    console.error(`Hubo un error: Error uploading ${file.name}:`, uploadError)
    throw uploadError
  }
}

/**
 * Uploads a file to S3 and confirms the update (for edit operations)
 * @param file - The file to upload
 * @param uploadUrl - The presigned URL for upload
 * @param statusAccountId - The status account ID for confirmation
 * @param apiService - The API service instance
 * @param contentType - The content type of the file (optional, defaults to file.type)
 * @returns Promise<void>
 */
export const uploadFileToS3AndConfirmUpdate = async (
  file: File,
  uploadUrl: string,
  statusAccountId: string,
  apiService: ApiService,
  contentType?: string,
): Promise<void> => {
  try {
    // Upload file to S3
    const uploadResponse = await uploadFileToS3(file, uploadUrl, contentType)

    console.log(`File ${file.name} Upload Response:`, uploadResponse)
    console.log(`File ${file.name} Upload Status:`, uploadResponse.status)
    console.log(`File ${file.name} Upload OK:`, uploadResponse.ok)

    // Confirm update was successful
    await apiService.confirmUpdated(statusAccountId, file.name, file.type)
    console.log(`File ${file.name} update confirmed`)
  } catch (uploadError) {
    console.error(`Hubo un error: Error uploading ${file.name}:`, uploadError)
    throw uploadError
  }
}
